import { LazyAtlassianNavigation } from '@atlassian/jira-atlassian-navigation/src/async';
import { CLASSIC_BOARD, CLASSIC_BACKLOG } from '@atlassian/jira-capabilities';
import { ff } from '@atlassian/jira-feature-flagging';
import { componentWithFF } from '@atlassian/jira-feature-flagging-utils';
import { genericProjectLayout } from '@atlassian/jira-generic-project-layout';
import { MENU_ID } from '@atlassian/jira-navigation-apps-common';
import { classicBoardSidebars } from '@atlassian/jira-navigation-apps-sidebar-generic-sidebars';
import { composeLayouts } from '@atlassian/jira-route-layout';
import { type Route, createEntry } from '@atlassian/jira-router';
import {
	getRapidboardResources,
	getRapidBoardDataResource,
} from '@atlassian/jira-router-resources-classic-projects';
import {
	getNavigationResources,
	getNavigationSidebarProjectResource,
	getUserBoardNavigationResources,
} from '@atlassian/jira-router-resources-navigation';
import { resourceWithCondition2 } from '@atlassian/jira-router-resources-utils';
import { LazyIssueApp } from '@atlassian/jira-router-routes-common';
import {
	rapidboardBacklogSettingsRoute,
	rapidboardBoardSettingsRoute,
	userBoardSettingsRoute,
	userBacklogSettingsRoute,
} from '@atlassian/jira-router-routes-software-classic-board-settings-routes';
import {
	LazyRapidBoard,
	LazyRapidBoardApps,
	LazyRapidBoardResource,
	LazyRapidBoardConfigApiResource,
	LazyRapidBoardDataApiResource,
	LazyRapidBoardWrmBundleResource,
	RapidBoardSpaPage,
} from '@atlassian/jira-router-routes-user-board-entries/src/async';
import { ClassicBoardSkeleton, NextGenBacklogSkeleton } from '@atlassian/jira-skeletons';
import { AsyncHorizontalSoftwareProjectNav } from '@atlassian/jira-software-horizontal-nav';
import { BoardConfigSwitcher } from './async';

const isModernisedBoardSettingsDisabled = () => !ff('simplified-boards-milestone-2_07hbk');

/* this is just a temporary package
 once the feature flag is rolled out fully we can split the settings
 into individual routes and avoid transition performance regression */
export const classicSoftwareBoardSettingsRouteEntries: Route[] = [
	// backlog route needs to be before board route
	createEntry(rapidboardBacklogSettingsRoute, {
		ufoName: 'rapidboard-backlog-settings',
		component: componentWithFF(
			'simplified-boards-milestone-2_07hbk',
			BoardConfigSwitcher,
			RapidBoardSpaPage,
		),
		skeleton: NextGenBacklogSkeleton,
		layout: composeLayouts(genericProjectLayout),

		navigation: {
			menuId: MENU_ID.PROJECTS,
			sidebar: classicBoardSidebars,
			horizontal: AsyncHorizontalSoftwareProjectNav,
			horizontalFeatureFlag: 'sea-2744-jsw-tabs-nav',
		},

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarProjectResource(),
			...getRapidboardResources(),
			resourceWithCondition2(isModernisedBoardSettingsDisabled, getRapidBoardDataResource()),
		],
		forPaint: [
			LazyAtlassianNavigation,
			LazyRapidBoard,
			LazyRapidBoardApps,
			LazyRapidBoardDataApiResource,
			LazyRapidBoardConfigApiResource,
			LazyRapidBoardWrmBundleResource,
			LazyRapidBoardResource,
		],

		meta: {
			capability: CLASSIC_BACKLOG,
		},
	}),
	createEntry(rapidboardBoardSettingsRoute, {
		ufoName: 'rapidboard-board-settings',
		component: componentWithFF(
			'simplified-boards-milestone-2_07hbk',
			BoardConfigSwitcher,
			RapidBoardSpaPage,
		),
		skeleton: ClassicBoardSkeleton,

		layout: composeLayouts(genericProjectLayout),

		navigation: {
			menuId: MENU_ID.PROJECTS,
			sidebar: classicBoardSidebars,
			horizontal: AsyncHorizontalSoftwareProjectNav,
			horizontalFeatureFlag: 'sea-2744-jsw-tabs-nav',
		},

		resources: [
			...getNavigationResources(),
			...getNavigationSidebarProjectResource(),
			...getRapidboardResources(),
			resourceWithCondition2(isModernisedBoardSettingsDisabled, getRapidBoardDataResource()),
		],

		afterPaint: [LazyIssueApp],

		forPaint: [
			LazyAtlassianNavigation,
			LazyRapidBoard,
			LazyRapidBoardApps,
			LazyRapidBoardDataApiResource,
			LazyRapidBoardConfigApiResource,
			LazyRapidBoardWrmBundleResource,
			LazyRapidBoardResource,
		],

		preloadOptions: {
			earlyChunks: ['rapidboard-early-entry'],
		},

		meta: {
			capability: CLASSIC_BOARD,
		},
	}),
	// backlog route needs to be before board route
	createEntry(userBacklogSettingsRoute, {
		component: componentWithFF(
			'simplified-boards-milestone-2_07hbk',
			BoardConfigSwitcher,
			RapidBoardSpaPage,
		),
		skeleton: NextGenBacklogSkeleton,
		layout: composeLayouts(genericProjectLayout),
		navigation: {
			menuId: MENU_ID.PROJECTS,
			sidebar: classicBoardSidebars,
			horizontal: AsyncHorizontalSoftwareProjectNav,
			horizontalFeatureFlag: 'sea-2744-jsw-tabs-nav',
		},
		resources: [
			...getNavigationResources(),
			...getNavigationSidebarProjectResource(),
			...getRapidboardResources(),
			...getUserBoardNavigationResources(),
			resourceWithCondition2(isModernisedBoardSettingsDisabled, getRapidBoardDataResource()),
		],
		forPaint: [
			LazyAtlassianNavigation,
			LazyRapidBoard,
			LazyRapidBoardApps,
			LazyRapidBoardDataApiResource,
			LazyRapidBoardConfigApiResource,
			LazyRapidBoardWrmBundleResource,
			LazyRapidBoardResource,
		],
	}),
	createEntry(userBoardSettingsRoute, {
		component: componentWithFF(
			'simplified-boards-milestone-2_07hbk',
			BoardConfigSwitcher,
			RapidBoardSpaPage,
		),
		skeleton: ClassicBoardSkeleton,
		layout: composeLayouts(genericProjectLayout),
		navigation: {
			menuId: MENU_ID.PROJECTS,
			sidebar: classicBoardSidebars,
			horizontal: AsyncHorizontalSoftwareProjectNav,
			horizontalFeatureFlag: 'sea-2744-jsw-tabs-nav',
		},
		resources: [
			...getNavigationResources(),
			...getNavigationSidebarProjectResource(),
			...getRapidboardResources(),
			...getUserBoardNavigationResources(),
			resourceWithCondition2(isModernisedBoardSettingsDisabled, getRapidBoardDataResource()),
		],
		forPaint: [
			LazyAtlassianNavigation,
			LazyRapidBoard,
			LazyRapidBoardApps,
			LazyRapidBoardDataApiResource,
			LazyRapidBoardConfigApiResource,
			LazyRapidBoardWrmBundleResource,
			LazyRapidBoardResource,
		],
	}),
];
